import { ColDef, GridOptions, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import {
  InvoiceDetail,
  InvoiceHeader,
  ItemDetail,
  ItemDetailStatusEnum,
  JoHeader,
  Shipment,
  ShippingBox,
} from "app/model/entities/entity-model";
import { InvoiceStatusCode } from "app/model/enums/invoice-status-code";
import { AddressDialogComponent } from "app/shared/address-dialog.component";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { DateFns } from "app/shared/date-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { NavFns } from "app/shared/nav-fns";
import { UnitOfWork } from "app/shared/unit-of-work";
import { ShippingService } from "app/shipping/shipping.service";
import { environment } from "environments/environment";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";
import { InvoiceShipmentService } from "./invoice-shipment.service";
import { JoStatusCode } from "@/model/enums/jo-status-code";
import { UniAgFns } from "@/shared/uni-ag-fns";

@Component({
  selector: "app-jo-invoice",
  templateUrl: "./jo-invoice.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class JoInvoiceComponent extends DomainBaseComponent {
  @ViewChild("shipLabelCell") shipLabelCell: TemplateRef<any>;
  joHeaderId: number;
  joHeader: JoHeader;
  joHeaders: JoHeader[]; // there will be only one - need an array for grid
  johGridOptions: GridOptionsExt;

  invoiceHeaders: InvoiceHeader[];
  invoiceHeader: InvoiceHeader;
  invhGridOptions: GridOptionsExt;
  initInvoiceId: string;

  invoiceDetails: InvoiceDetail[];
  invdGridOptions: GridOptionsExt;

  itemDetails: ItemDetail[];
  itdGridOptions: GridOptionsExt;

  shippingBoxes: ShippingBox[] = [];
  sboxGridOptions: GridOptionsExt;

  vouchersWithSameAddress: InvoiceHeader[];
  shippingIdKey: string;
  canEditJoAddresses = false;

  uomMap = {
    oz: "Ounces",
    lb: "Pounds",
    gr: "Grams",
    kg: "Kilograms",
  };

  constructor(
    public uow: UnitOfWork,
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected shippingService: ShippingService,
    protected invoiceShipmentService: InvoiceShipmentService,
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.joHeaderId = +this.route.snapshot.params["joHeaderId"];
    this.initInvoiceId = this.route.snapshot.params["invoiceId"];

    this.johGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onJohGridReady,
    });

    this.invhGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onInvhGridReady,
      onRowSelected: this.onInvhRowSelected,
      onFirstDataRendered: (params) => this.AgFns.selectFirstRow(this.invhGridOptions),
    });

    this.itdGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onItdGridReady,
    });

    this.invdGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onInvdGridReady,
      },
      { detailProperty: "joDetail.joDetailAddons" },
    );
    this.updateInvdMasterDetail(this.invdGridOptions);

    this.sboxGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onSboxGridReady,
    });

    const p1 = this.dbQueryService.getJoHeaderWithInvInfoById(this.joHeaderId).then((r) => {
      this.joHeader = r;
      this.joHeaders = r != null ? [r] : [];
      this.invoiceHeaders = this.joHeader.invoiceHeaders;
    });

    // we need the itemDetails but we don't need them explicitly as they will automatically be attached to the joHeader
    // query handles both joDetail and accountPurchaseJoDetail linked itemDetails
    const p2 = this.dbQueryService.getItemDetailsByJoHeaderId(this.joHeaderId);
    await Promise.all([p1, p2]);

    await this.fixupInvoices(this.invoiceHeaders);

    await this.shippingService.getShippingCarriers(); // just to cache everything

    this.canEditJoAddresses = this.domainService.authService.getUser().isAccountAdmin;

    this.isPageReady = true;
  }

  // Hack: needed to insure that vouchers have a valid due date and vouchers have calc'd totals
  private async fixupInvoices(invHeaders: InvoiceHeader[]) {
    const voucherStatusIds = [InvoiceStatusCode.Voucher, InvoiceStatusCode.VoucherLabeled, InvoiceStatusCode.VoidError];
    const appConfig = this.dbQueryService.appConfig;
    invHeaders.forEach((invh) => {
      if (invh.invoiceDate.getTime() >= invh.dueDate.getTime()) {
        const dueDays = invh.joHeader.account.dueDateNumDays || appConfig.account.dueDateNumDays;
        invh.dueDate = DateFns.dateAdd(invh.invoiceDate, dueDays, "days");
      }

      // needed because vouchers did not get migrated with a total ( deliberately - we wanted to calculate them here)
      if (voucherStatusIds.includes(<InvoiceStatusCode>invh.invoiceStatusId)) {
        const subtotal = invh.calcExtendedAmt() ?? invh.subtotalAmt ?? 0;
        const reissue = invh.calcExtendedReissueAmt() ?? 0;
        invh.totalAmt = subtotal + reissue + (invh.taxAmt ?? 0) + (invh.orderHandlingAmt ?? 0) + (invh.freightAmt ?? 0) - (invh.prepaymentAmt ?? 0) - (invh.pCardAmt ?? 0);
      }
    });
    if (this.uow.hasChanges()) {
      await this.dbSaveService.saveChanges();
    }
  }

  onJohGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Job Order Id",
        ...NavFns.createIdCellClickedNavProps("id", this.router, "/in-process/manage/job-orders"),
      },
      { headerName: "Account", field: "account.accountName" },
      { headerName: "J/O Date", field: "joDate" },
      { headerName: "Ship By Date", field: "origExpectedShipDate", hide: this.joHeader.isAccountOwnedOrder },
      { ...AgFns.createCheckboxProps("Is Account-Owned Order", "isAccountOwnedOrder") },
      { headerName: "Status", field: "joStatus.name" },
    ];
    AgFns.initGrid(event, colDefs);
  }

  onInvhGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Invoice",
        field: "id",
        cellClass: "ag-left-aligned-cell",
        cellRenderer: "agGroupCellRenderer",
      },
      { headerName: "Invoice Date", field: "invoiceDate" },
      { headerName: "Due Date", field: "dueDate" },
      { headerName: "Status", field: "invoiceStatus.name" },
    ];

    const sortModel = [{ colId: "id", sort: "asc" as const }];
    AgFns.initGrid(event, colDefs, sortModel);

    this.invhGridOptions.onFirstDataRendered = () => {
      AgFns.selectGridRowByKey(this.invhGridOptions, (e) => e.id, this.initInvoiceId);
    };
  }

  /** invoiceDetails is always the selected invoiceHeader's, so the JO-level flag is read from there. */
  get chargeAddonsOnReissuance() {
    return this.invoiceHeader?.joHeader?.chargeAddonsOnReissuance ?? false;
  }

  onInvdGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      // {
      //   headerName: "Invoice Detail Id",
      //   field: "id",
      //   cellRenderer: "agGroupCellRenderer",
      // },
      // { headerName: "JoDetail Id", field: "joDetailId", type: "numericColumn" },
      { ...UniAgFns.getManufacturerColDef("joDetail.product.productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("joDetail.product.productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("joDetail.product.productType.description") },
      { ...UniAgFns.getFeatureChoicesColDef("joDetail.product.featureChoicesExtract") },

      { headerName: "New Qty", field: "shipQty", ...AgFns.fixWidth(100) },
      { headerName: "Price/Unit", field: "joDetail.unitPriceAmt", ...AgFns.fixWidth("money") },
      { headerName: "Oversize Amt/Unit", field: "joDetail.unitOversizeAmt", ...AgFns.fixWidth("money") },
      { headerName: "Custom Amt/Unit", field: "joDetail.unitCustomAmt", ...AgFns.fixWidth("money") },
      // Should be the same as unitCustomAmt
      // { headerName: 'Mods Amt/Unit', field: 'joDetail.unitModsAmt' },
      { headerName: "Total Amt/Unit", field: "joDetail.unitTotalAmt", ...AgFns.fixWidth("money") },
      {
        headerName: "Extended Amt",
        valueGetter: (params) => {
          return params.data.joDetail.unitTotalAmt * params.data.shipQty;
        },
        valueFormatter: (params) => {
          return this.UtilFns.fmtCurrency(params.value);
        },
        ...AgFns.fixWidth("money"),
        type: "numericColumn",
      },
      { headerName: "Reissue Qty", field: "redistributeQty", ...AgFns.fixWidth(100) },
      {
        headerName: "Reissue Amt/Unit",
        valueGetter: (params) => {
          return params.data.calcUnitReissueAmt(this.chargeAddonsOnReissuance);
        },
        valueFormatter: (params) => {
          return this.UtilFns.fmtCurrency(params.value);
        },
        ...AgFns.fixWidth("money"),
        type: "numericColumn",
      },
      {
        headerName: "Extended Reissue Amt",
        valueGetter: (params) => {
          return params.data.calcExtendedReissueAmt(this.chargeAddonsOnReissuance);
        },
        valueFormatter: (params) => {
          return this.UtilFns.fmtCurrency(params.value);
        },
        ...AgFns.fixWidth("money"),
        type: "numericColumn",
      },
    ];

    AgFns.initGrid(event, colDefs);
  }

  updateInvdMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: "Addon", field: "addon.nameAndLocation" },
      { headerName: "Addl. Info", field: "additionalInfo" },
      { headerName: "Price/Unit", field: "unitPriceAmt" },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params) => {
        const invd = params.data as InvoiceDetail;
        params.successCallback(invd.joDetail.joDetailAddons);
      },
    };
  }

  onItdGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...UniAgFns.getManufacturerColDef("product.productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("product.productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("product.productType.description") },
      { ...UniAgFns.getFeatureChoicesColDef("product.featureChoicesExtract") },

      { headerName: "Status", field: "itemDetailStatus.name", ...AgFns.fixWidth(1560) },
      {
        headerName: "Inventory Identifier",
        field: "id",
        cellClass: "ag-left-aligned-cell",
        ...AgFns.fixWidth(150),
      },
    ];
    AgFns.initGrid(event, colDefs);
  }

  onSboxGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: "Box Id", field: "id" },
      {
        headerName: "Weight",
        field: "weight",
        type: "numericColumn",
        valueGetter: (params) => {
          const sb = <ShippingBox>params.data;
          return `${sb.weight} ${this.uomMap[sb.weightUoMId]}`;
        },
      },
      { headerName: "Package", field: "packageApiIdentifier" },
      {
        headerName: "Tracking #",
        field: "trackingLabelApiIdentifier",
        cellRenderer: this.renderTrackingLink.bind(this),
      },
      { ...AgFns.createCellButtonProps("Shipping Label", this.shipLabelCell) },
    ];
    AgFns.initGrid(event, colDefs);
  }

  async onInvhRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }

    const invh = e.data as InvoiceHeader;
    if (!invh) {
      return;
    }
    this.invoiceHeader = invh;
    this.invoiceDetails = invh.invoiceDetails;

    this.itemDetails = this.joHeader.isAccountOwnedOrder ? invh.accountPurchaseItemDetails : invh.itemDetails;
    this.updateLocation(invh.id);

    let pShipment = Promise.resolve(<Shipment>null);
    if (this.invoiceHeader?.shipmentId != null) {
      pShipment = this.dbQueryService.getShipment(this.invoiceHeader.shipmentId);
    }
    const pVouchers = this.dbQueryService.getVoucherInvoicesOnAccount(this.joHeader.accountId);

    let [shipment, relatedVouchers] = await Promise.all([pShipment, pVouchers]);
    if (shipment != null) {
      this.updateShippingBoxUI(shipment);
    }

    relatedVouchers = relatedVouchers.filter((invh) => invh.joHeader.shippingKey == this.invoiceHeader.joHeader.shippingKey);

    this.vouchersWithSameAddress = relatedVouchers.filter(
      (invh) => (invh.shipmentId == null || invh.shipmentId != this.invoiceHeader.shipmentId) && invh.id != this.invoiceHeader.id,
    );
    this.shippingIdKey = _.min(relatedVouchers.map((invh) => invh.id));
  }

  onTabChanged(event: any) {
    const ix = event.index;
    if (ix == 3) {
      AgFns.sizeColumnsToFit(this.invdGridOptions);
    } else if (ix == 4) {
      AgFns.sizeColumnsToFit(this.itdGridOptions);
    }
  }

  updateLocation(key: any = null) {
    const urlTree = this.router.createUrlTree([`/in-process/manage/jo-invoice/${this.joHeaderId}/${key}`]);
    const url = urlTree.toString();
    this.domainService.location.replaceState(url);
  }

  shouldShowMigratedTotal(invoiceHeader: InvoiceHeader) {
    return invoiceHeader.wasMigrated && Math.abs(invoiceHeader.totalAmt - invoiceHeader.calcTotalAmt()) > 0.01;
  }

  formatInvoiceList(invoiceHeaders: InvoiceHeader[]) {
    return invoiceHeaders.map((invh) => invh.id).join(", ");
  }

  /** link to carrier site to track packages */
  renderTrackingLink(params: any) {
    const box = params.data as ShippingBox;
    const link = this.shippingService.getTrackingLink(box);
    if (!link) {
      return box.trackingLabelApiIdentifier;
    }
    return `<a style="color:blue;" href="${link}" target="_blank" rel="noopener">${box.trackingLabelApiIdentifier}</a>`;
  }

  canUndoInvoice(invoiceHeader: InvoiceHeader) {
    // isPickup as well as hasShippingLabel: a pickup-released invoice is already sitting on
    // End of Day with no carrier label, so hasShippingLabel alone would leave it undoable.
    return invoiceHeader?.isNotPosted && !this.invoiceHeader?.hasShippingLabel && !this.invoiceHeader?.isPickup;
  }

  async undoInvoice(invoiceHeader: InvoiceHeader) {
    // put the related itemDetail back into the Pull or Received state
    // need to do this before we detach the invoiceHeader.
    const itemDetails = invoiceHeader.itemDetails.filter((itd) => itd.invoiceHeaderId === invoiceHeader.id);
    itemDetails.forEach((itd) => {
      // move item back to ShipPrep
      itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessShipPrep;
      itd.invoiceHeaderId = null;
    });

    // insure that UI cannot update during next section
    this.invoiceHeader = null;

    await this.undoShipment(invoiceHeader);

    invoiceHeader.invoiceDetails.slice().forEach((invd) => {
      invd.entityAspect.setDeleted();
    });
    invoiceHeader.entityAspect.setDeleted();

    await this.dbSaveService.saveChanges();

    this.toastr.success("Invoice undone");

    const qparams = NavFns.buildNavIdParams(this.joHeaderId, "joDetail.joHeader.id");
    this.router.navigate(["/in-process/manage/scan-station"], qparams);
  }

  private async undoShipment(invoiceHeader: InvoiceHeader) {
    if (invoiceHeader.shipmentId == null) {
      return;
    }

    const shipment = await this.dbQueryService.getShipment(invoiceHeader.shipmentId);
    if (shipment == null) {
      // means that shipment was probably deleted by another session
      return;
    }

    if (invoiceHeader.shipment.primaryInvoiceHeaderId != invoiceHeader.id) {
      const ok = await this.dialogService.yesNoDialog(
        "Delete shipment info?",
        `This invoice is not the primary invoice for this shipment.
        Do you want to delete this shipment or just remove this invoice from the shipment? `,
        "Delete Shipment",
        "Remove Invoice from Shipment",
      );
      if (ok) {
        invoiceHeader.shipmentId = null;
        return;
      }
    }

    shipment.invoiceHeaders.forEach((invh) => (invh.shipmentId = null));
    // delete shipping info
    shipment.shippingBoxes.slice().forEach((sb) => sb.entityAspect.setDeleted());
    shipment.entityAspect.setDeleted();
  }

  canCalcFreight(invoiceHeader: InvoiceHeader) {
    return (
      invoiceHeader &&
      (invoiceHeader?.isPrimaryInvoice || invoiceHeader?.shipment == null) &&
      invoiceHeader.isNotPosted &&
      !invoiceHeader.hasShippingLabel &&
      !invoiceHeader.isPickup
    );
  }

  async calcFreight(invoiceHeader: InvoiceHeader) {
    const ok = await this.invoiceShipmentService.askAndCalcFreight(invoiceHeader);
    if (ok) {
      this.updateShippingBoxUI(invoiceHeader.shipment);
    }
  }

  updateShippingBoxUI(shipment: Shipment) {
    this.shippingBoxes = shipment.shippingBoxes;
    // Hack - this should NOT be necessary but if the Freight tab is live and
    // we recalc and modify the shippingBoxes then they do NOT repaint unless we
    // set everything to [] and then back.
    this.sboxGridOptions.apiX?.setGridOption("rowData", []);
    this.sboxGridOptions.apiX?.setGridOption("rowData", this.shippingBoxes);
  }

  canPrintLabel(invoiceHeader: InvoiceHeader) {
    return (
      (invoiceHeader?.invoiceStatusId == InvoiceStatusCode.Voucher || invoiceHeader?.invoiceStatusId == InvoiceStatusCode.VoucherLabeled) &&
      invoiceHeader.hasFreight
    );
  }

  async printBoxLabel(box: ShippingBox) {
    if (!box.shippingLabel) {
      return;
    }
    return await this.invoiceShipmentService.printBoxShippingLabels([box]);
  }

  async printLabels(invoiceHeader: InvoiceHeader) {
    const ok = await this.invoiceShipmentService.printShippingLabel(invoiceHeader);
    if (ok) {
      this.invhGridOptions.apiX?.redrawRows();
      this.sboxGridOptions.apiX?.redrawRows();
    }
  }

  canVoidLabel(invoiceHeader: InvoiceHeader) {
    return (
      invoiceHeader?.isPrimaryInvoice && invoiceHeader.hasShippingLabel && invoiceHeader.invoiceStatusId == InvoiceStatusCode.VoucherLabeled
    );
  }

  async voidLabel(invoiceHeader: InvoiceHeader) {
    const ok = await this.invoiceShipmentService.voidShippingLabel(invoiceHeader);
    // we may have a void error - in which case we need to also change ui
    this.invhGridOptions.apiX?.redrawRows();
    this.sboxGridOptions.apiX?.redrawRows();
  }

  canRemoveVoidError(invoiceHeader: InvoiceHeader) {
    return invoiceHeader?.isPrimaryInvoice && invoiceHeader?.invoiceStatusId === InvoiceStatusCode.VoidError;
  }

  async removeVoidError(invoiceHeader: InvoiceHeader) {
    const ok = await this.invoiceShipmentService.removeVoidError(invoiceHeader);
    if (ok) {
      this.invhGridOptions.apiX?.redrawRows();
      this.sboxGridOptions.apiX?.redrawRows();
    }
  }

  canEditAddress(prefix: string) {
    if (!this.canEditJoAddresses || this.joHeader?.joStatusId == JoStatusCode.Closed) {
      return false;
    }
    if (this.invoiceHeader?.isPostedOrQueued) {
      return false;
    }
    if (prefix == "shipping") {
      if (this.invoiceHeader?.hasShippingLabel) {
        // need to void label first
        return false;
      }
    }
    return true;
  }

  async editAddress(prefix: string, addressTypeName: string) {
    const r = await DialogBaseComponent.open(AddressDialogComponent, this, {
      entity: this.invoiceHeader.joHeader,
      addressPrefix: prefix,
      addressTypeName: addressTypeName,
      editable: true,
    });
    // if we change the address we must recalc the freight
    if (r && prefix == "shipping") {
      const shipment = this.invoiceHeader.shipment;
      if (shipment != null) {
        shipment.freightAmt = null;
        shipment.shipmentApiIdentifier = null;
      }
    }
  }

  canDisplayAltVoucherMessage() {
    return this.vouchersWithSameAddress?.length > 0 && this.invoiceHeader?.isNotPosted;
  }

  getCarrierDisplayName(invoiceHeader: InvoiceHeader) {
    return this.shippingService.getCarrierDisplayName(invoiceHeader.shipment.carrierApiIdentifier);
  }

  getServiceDisplayName(invoiceHeader: InvoiceHeader) {
    return this.shippingService.getServiceDisplayName(
      invoiceHeader.shipment.carrierApiIdentifier,
      invoiceHeader.shipment.serviceApiIdentifier,
    );
  }

  getPcardName(invoiceHeader: InvoiceHeader) {
    const name = invoiceHeader.pCardName;
    if (!name || !name.indexOf(" ")) {
      return name;
    }
    return name.replace("XXXX", "");
  }

  getQuickbooksLink(invoiceHeader: InvoiceHeader) {
    if (invoiceHeader.invoiceStatusId === "POSTED") {
      return environment.quickbooksUrl + "invoice?txnId=" + invoiceHeader.gLInvoiceId;
    }
    return "";
  }

  // goShipping() {
  //   const params = {
  //     queryParams: {
  //       key: this.shippingIdKey,
  //     },
  //   };
  //   this.router.navigate(["/jo-ship"], params);
  // }

  goPrintableInvoice() {
    if (this.invoiceHeader != null) {
      this.router.navigate(["/printable-invoice", this.invoiceHeader.id]);
    }
  }
}
